/**
 * Blog Post: Welcome to Builder Buddy
 * Author: Friendly Guide (Sarah Mitchell)
 */

import type { BlogPost } from "../types";

export const welcomePost: BlogPost = {
  slug: "welcome-to-builder-buddy",
  title: "Welcome to Builder Buddy: Your AI Building Code Assistant",
  description:
    "Discover how Builder Buddy helps Calgary homeowners, contractors, and developers navigate building codes, permits, and construction planning with AI-powered tools.",
  excerpt:
    "Whether you're planning a basement renovation or a new commercial build, Builder Buddy is here to help you understand building codes and make informed decisions.",
  author: "friendly-guide",
  category: "getting-started",
  featuredImage: "/blog/welcome-hero.jpg",
  tags: [
    "introduction",
    "Calgary",
    "building codes",
    "AI assistant",
    "getting started",
  ],
  publishedAt: "2026-01-26",
  readingTime: 5,
  tableOfContents: [
    { id: "what-is-builder-buddy", title: "What is Builder Buddy?", level: 2 },
    { id: "who-is-it-for", title: "Who is it for?", level: 2 },
    { id: "our-features", title: "Our Features", level: 2 },
    { id: "getting-started", title: "Getting Started", level: 2 },
  ],
  callToAction: {
    title: "Ready to get started?",
    description: "Try our AI Chat to ask your first building code question.",
    buttonText: "Start Chatting",
    buttonLink: "/chat",
  },
  content: `
Welcome to Builder Buddy! We're excited to have you here. Whether you're a first-time homeowner planning a basement renovation, a seasoned contractor working on multiple projects, or a developer navigating Calgary's zoning requirements, we've built this platform with you in mind.

## What is Builder Buddy?

Builder Buddy is a free, AI-powered building code assistant designed specifically for Calgary, Alberta, and Canada. Our goal is simple: **make building codes accessible and understandable for everyone**.

Building codes can be intimidating. The National Building Code alone has thousands of pages of requirements. Add in provincial amendments, municipal bylaws, and fire codes, and it's easy to feel overwhelmed. That's where we come in.

We've combined artificial intelligence with comprehensive building code databases to create tools that give you instant, accurate answers to your questions. No more digging through PDFs or calling multiple offices - just clear, cited responses you can trust.

## Who is it for?

**Homeowners** - Planning a renovation? Wondering if you need a permit for that deck? Curious about basement development requirements? We'll help you understand what's required before you start.

**Contractors and Builders** - Need quick code references on the job site? Want to verify setback requirements or fire ratings? Our tools give you instant access to the information you need.

**Architects and Designers** - Use our code library to quickly search across NBC, NPC, NFC, and Calgary bylaws. Find specific articles and export citations for your documentation.

**Developers** - Planning a multi-family project or infill development? Our SDAB expert tool can help you assess appeal risk, and our quantity survey calculator provides accurate cost estimates.

## Our Features

**AI Chat** - Ask questions in plain English and get answers with specific code citations. "What's the minimum stair width?" "Do I need a permit for a deck?" Our AI understands context and provides relevant, accurate responses.

**Code Library** - Search and browse the complete building code database including NBC 2023 Alberta Edition, National Plumbing Code, National Fire Code, Energy Code, and Calgary's Land Use Bylaw.

**Permit Checker** - Enter your project details and find out exactly what permits you need in Calgary. Get estimated fees, processing times, and required documents.

**SDAB Expert** - Thinking about applying for a variance or setback relaxation? Our expert system analyzes your project and predicts appeal outcomes based on historical SDAB decisions.

**Quantity Survey Calculator** - Get accurate construction cost estimates for Calgary. Our calculator uses RSMeans data with local adjustment factors for reliable budgeting.

**Feasibility Study** - Compare material sourcing options from Canada, USA, and China. Understand tariff impacts, lead times, and find government incentives.

## Getting Started

The best way to start is simply to ask a question. Head over to our **AI Chat** and type something like:

- "What permits do I need for a basement development?"
- "What's the minimum ceiling height for a bedroom?"
- "What fire separation is required between garage and house?"

Our AI will provide an answer with specific code citations you can reference.

If you want to dive deeper into specific codes, check out the **Code Library** where you can search and browse all the building codes we cover.

For a more structured approach to your project, try the **Permit Checker** - just enter your project type and specifications, and we'll tell you everything you need to know about the permit process.

We're here to help make your construction project successful. Welcome to Builder Buddy!
`,
};
